import React from "react";
import { Link } from "react-router-dom";
import PageContext from "../PageContext.jsx";

const setupSteps = [
  {
    title: "Set up your working entity",
    body: "Open Settings first, confirm the current working entity, and rename it to something meaningful such as a household, business, or project name.",
    links: [
      { to: "/Settings", label: "Open Settings" },
      { to: "/Entities", label: "Manage Entities" },
    ],
  },
  {
    title: "Define currencies before entering values",
    body: "Create the default currency you want reports to use, then add any secondary currencies with exchange rates to the default.",
    links: [
      { to: "/Currencies", label: "Review Currencies" },
      { to: "/CurrenciesAdd", label: "Add Currency" },
    ],
  },
  {
    title: "Create your reporting periods",
    body: "Add the monthly or periodic windows you want to report against. Most later reporting depends on periods being in place first.",
    links: [
      { to: "/Periods", label: "Review Periods" },
      { to: "/PeriodsAdd", label: "Add Period" },
    ],
  },
  {
    title: "Build the transaction structure",
    body: "Set up categories, subcategories, and transaction types. Mark savings categories correctly so dashboards and savings-related reports behave as expected.",
    links: [
      { to: "/Categories", label: "Categories" },
      { to: "/Subcategories", label: "Subcategories" },
      { to: "/TransactionTypes", label: "Transaction Types" },
    ],
  },
  {
    title: "Enter accounts and balances",
    body: "Create banks, loans, and assets before loading transactions. For banks and loans, capture dated balances so net worth and passive income calculations have a baseline.",
    links: [
      { to: "/Banks", label: "Banks" },
      { to: "/BankBalances", label: "Bank Balances" },
      { to: "/Loans", label: "Loans" },
      { to: "/Assets", label: "Assets" },
    ],
  },
  {
    title: "Load transactions for each period",
    body: "Once the structure is ready, enter transactions and review them. This is the data that will drive most of the analysis pages.",
    links: [
      { to: "/TransactionsAdd", label: "Enter Transaction" },
      { to: "/Transactions", label: "Review Transactions" },
    ],
  },
  {
    title: "Refresh period and net worth figures",
    body: "After data entry, update period totals where needed and recalculate net worth from the latest balances and asset values.",
    links: [
      { to: "/Periods", label: "Review Period Totals" },
      { to: "/NetWorth", label: "Net Worth" },
    ],
  },
  {
    title: "Use reports last",
    body: "Start with the dashboard for a top-level check, then move into passive income, period summaries, and spending trends once the underlying setup is complete.",
    links: [
      { to: "/ReportsDashboard", label: "Dashboard" },
      { to: "/Reports", label: "All Reports" },
    ],
  },
];

const GettingStarted = () => {
  return (
    <div className="page">
      <header className="page-header">
        <div>
          <h1 className="page-title">Getting Started</h1>
          <p className="page-subtitle">
            <Link to="/">Home</Link> / Getting Started
          </p>
        </div>
      </header>
      <PageContext
        items={[
          { label: "Home", to: "/" },
          { label: "Getting Started" },
        ]}
        note="Suggested setup order for a new user so the reports and dashboard have clean source data."
      />

      <section className="panel">
        <div className="section-heading">
          <div>
            <p className="eyebrow">Suggested Order</p>
            <h2>Recommended first-time setup</h2>
          </div>
          <p className="section-copy">
            Follow this sequence once per new entity. It reduces rework and makes the downstream reports more reliable.
          </p>
        </div>
        <div className="setup-step-list">
          {setupSteps.map((step, index) => (
            <article key={step.title} className="setup-step-card">
              <div className="setup-step-number">{index + 1}</div>
              <div className="setup-step-content">
                <h3>{step.title}</h3>
                <p>{step.body}</p>
                <div className="inline-links">
                  {step.links.map((item) => (
                    <Link key={item.to} className="btn-link" to={item.to}>
                      {item.label}
                    </Link>
                  ))}
                </div>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="panel page-cta">
        <div className="section-heading">
          <div>
            <p className="eyebrow">Quick Checks</p>
            <h2>Before trusting the numbers</h2>
          </div>
        </div>
        <div className="dashboard-grid">
          <div className="metric-card metric-card-compact">
            <strong>Default Currency</strong>
            <div className="resource-meta">Set one default currency and confirm exchange rates for all others.</div>
          </div>
          <div className="metric-card metric-card-compact">
            <strong>Savings Categories</strong>
            <div className="resource-meta">Mark savings categories correctly so expenditure metrics exclude them.</div>
          </div>
          <div className="metric-card metric-card-compact">
            <strong>Period Totals</strong>
            <div className="resource-meta">Refresh or verify period totals before relying on dashboard averages.</div>
          </div>
          <div className="metric-card metric-card-compact">
            <strong>Latest Balances</strong>
            <div className="resource-meta">Make sure bank and loan balances are current before recalculating net worth.</div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default GettingStarted;
